import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, CalendarDots, Cake, ArrowRight, MapPin, VideoCamera, WhatsappLogo, Warning } from '@phosphor-icons/react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const WD = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const avatarUrl = (p) => (p ? (p.startsWith('http') ? p : `${API}/api/files/${p}`) : '');
const initials = (name) => (name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const fmtLongDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return `${WD[d.getDay()]}, ${d.getDate()} de ${MONTHS[d.getMonth()]}`;
};

function Avatar({ path, name, size = 48 }) {
  const url = avatarUrl(path);
  if (url) return <img src={url} alt={name} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full flex items-center justify-center text-white font-heading font-semibold flex-shrink-0 bg-brand-green"
      style={{ width: size, height: size, fontSize: size * 0.36 }}>{initials(name)}</span>
  );
}

function ModalityTag({ modality }) {
  const online = modality === 'online';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${online ? 'bg-brand-mint/40 text-brand-green' : 'bg-brand-gold/20 text-brand-gold-hover'}`}>
      {online ? <VideoCamera size={11} weight="fill" /> : <MapPin size={11} weight="fill" />}
      {online ? 'online' : 'presencial'}
    </span>
  );
}

export default function PainelInicialPage() {
  useRequireAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/admin/home`); setData(data); }
      catch {} finally { setLoading(false); }
    })();
  }, []);

  const openFicha = (pid) => { if (pid) navigate(`/admin/pacientes/${pid}`); };

  if (loading) return <div className="text-center py-16 text-brand-muted" data-testid="painel-loading">A carregar...</div>;

  const na = data?.next_appointment;

  return (
    <div className="fade-in max-w-3xl mx-auto space-y-6" data-testid="painel-inicial-page">
      <div>
        <h1 className="font-heading font-medium text-2xl sm:text-3xl text-brand-text" data-testid="painel-greeting">
          {data?.greeting}{data?.first_name ? `, ${data.first_name}` : ''} 👋
        </h1>
        <p className="text-sm text-brand-muted mt-1">Aqui tens as tuas próximas consultas.</p>
      </div>

      {/* Próxima consulta — destaque */}
      <section data-testid="painel-next-appointment">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-green/60 mb-2">Próxima consulta</h2>
        {na ? (
          <div className="bg-white border border-brand-border rounded-3xl p-5 shadow-[0_8px_32px_rgba(101,113,97,0.06)]">
            <div className="flex items-start gap-4">
              <Avatar path={na.avatar_path} name={na.patient_name} size={64} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-medium text-lg text-brand-text truncate">{na.patient_name}</h3>
                  <ModalityTag modality={na.modality} />
                </div>
                <p className="text-sm text-brand-muted mt-1 capitalize">
                  {fmtLongDate(na.date)} <span className="text-brand-border">|</span> {na.time}{na.end_time ? ` - ${na.end_time}` : ''}
                </p>
                {data?.countdown && <span className="inline-block mt-2 text-xs font-medium text-brand-green bg-brand-mint/30 px-2.5 py-1 rounded-full" data-testid="painel-countdown">{data.countdown}</span>}
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button onClick={() => openFicha(na.patient_id)} className="flex items-center justify-center gap-2 bg-brand-green text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-green-hover transition-colors" data-testid="painel-open-ficha">
                Abrir ficha <ArrowRight size={15} />
              </button>
              {na.whatsapp_url && (
                <a href={na.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-[#25D366] text-[#128C7E] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#25D366]/10 transition-colors" data-testid="painel-whatsapp-next">
                  <WhatsappLogo size={16} weight="fill" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-brand-border rounded-3xl p-8 text-center" data-testid="painel-next-empty">
            <CalendarCheck size={40} weight="duotone" className="mx-auto text-brand-placeholder mb-3" />
            <p className="text-sm text-brand-muted mb-4">Não tens consultas agendadas.</p>
            <button onClick={() => navigate('/admin/calendario')} className="inline-flex items-center gap-2 border border-brand-border text-brand-green rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-bg" data-testid="painel-empty-agenda-btn">
              <CalendarDots size={15} /> Abrir calendário
            </button>
          </div>
        )}
      </section>

      {/* Próximas consultas */}
      <section data-testid="painel-upcoming">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-green/60">Próximas consultas</h2>
          <button onClick={() => navigate('/admin/calendario')} className="text-xs text-brand-green hover:underline flex items-center gap-1" data-testid="painel-open-calendar">Abrir calendário <ArrowRight size={12} /></button>
        </div>
        {data?.upcoming?.length ? (
          <div className="space-y-2">
            {data.upcoming.map(a => (
              <div key={a.id} data-testid={`painel-upcoming-${a.id}`}
                className="w-full bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3 hover:shadow-md transition-shadow">
                <button onClick={() => openFicha(a.patient_id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`painel-upcoming-open-${a.id}`}>
                  <Avatar path={a.avatar_path} name={a.patient_name} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-sm text-brand-text truncate">{a.patient_name}</p>
                    <p className="text-xs text-brand-muted capitalize">{fmtLongDate(a.date)} · {a.time}</p>
                  </div>
                </button>
                <ModalityTag modality={a.modality} />
                {a.whatsapp_url && (
                  <a href={a.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[#128C7E] hover:bg-[#25D366]/10 transition-colors" title="Confirmar por WhatsApp" data-testid={`painel-whatsapp-${a.id}`}>
                    <WhatsappLogo size={18} weight="fill" />
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-brand-border rounded-2xl p-6 text-center" data-testid="painel-upcoming-empty">
            <p className="text-sm text-brand-muted mb-3">Não tens mais consultas agendadas</p>
            <button onClick={() => navigate('/admin/calendario')} className="inline-flex items-center gap-2 border border-brand-border text-brand-green rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-bg">
              <CalendarDots size={15} /> Abrir calendário
            </button>
          </div>
        )}
      </section>

      {/* Aniversários de hoje */}
      {data?.birthdays?.length > 0 && (
        <section data-testid="painel-birthdays">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-green/60 mb-2 flex items-center gap-1.5"><Cake size={14} weight="duotone" className="text-brand-gold-hover" /> Aniversários de hoje</h2>
          <div className="space-y-2">
            {data.birthdays.map(b => (
              <button key={b.patient_id} onClick={() => openFicha(b.patient_id)} data-testid={`painel-birthday-${b.patient_id}`}
                className="w-full text-left bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3 hover:shadow-md transition-shadow">
                <Avatar path={b.avatar_path} name={b.patient_name} size={40} />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm text-brand-text truncate">{b.patient_name}</p>
                  {b.age != null && <p className="text-xs text-brand-muted">Faz {b.age} anos hoje 🎉</p>}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* IVA art. 9.º — clientes sem finalidade terapêutica */}
      {(data?.missing_therapeutic_purpose_count || 0) > 0 && (
        <section data-testid="painel-missing-tp">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-600/80 mb-2 flex items-center gap-1.5">
            <Warning size={13} weight="fill" className="text-amber-500" /> IVA art. 9.º
          </h2>
          <button
            onClick={() => navigate('/admin/pacientes')}
            className="w-full text-left bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:shadow-md transition-shadow"
            data-testid="painel-missing-tp-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-semibold text-amber-700 text-lg">{data.missing_therapeutic_purpose_count}</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {data.missing_therapeutic_purpose_count === 1 ? 'cliente ativa sem' : 'clientes ativas sem'} finalidade terapêutica registada
                </p>
              </div>
              <ArrowRight size={18} className="text-amber-500 flex-shrink-0" />
            </div>
          </button>
        </section>
      )}
    </div>
  );
}
